import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import Checkbox from '@atlaskit/checkbox';
import Heading from '@atlaskit/heading';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Lozenge from '@atlaskit/lozenge';
import ModalDialog, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle
} from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Select, {
  type CSSObjectWithLabel,
  components as selectComponents,
  type MenuListComponentProps,
  type ValueType
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { Label } from '@databases/form/FormComponents';
import { AiSummaryIcon } from '@databases/icons/AiSummaryIcon';
import { messages } from '@databases/intl/messages';
import {
  type AiSummaryFieldConfig,
  type AiSummaryOutputFormat
} from '@databases/types/database';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

type FormatOption = { label: string; value: AiSummaryOutputFormat };

export type AiSummaryPreviewOption = {
  label: string;
  description?: string;
  entryId: string;
  value: number;
};

export type AiSummaryPreviewRequest = {
  abortSignal: AbortSignal;
  config: AiSummaryFieldConfig;
  entryId: string;
};

type AiSummaryPreviewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; summary: string };

const EMPTY_PREVIEW_OPTIONS: AiSummaryPreviewOption[] = [];
const MAX_PREVIEW_ENTRY_COUNT = 5;

export interface AiSummaryFieldModalProps {
  mode: 'add' | 'edit';
  initialConfig?: AiSummaryFieldConfig;
  previewOptions?: AiSummaryPreviewOption[];
  onClose: () => void;
  onPreviewMenuOpen?: () => void;
  onRegenerate?: (format: AiSummaryOutputFormat) => void;
  onGeneratePreview?: (request: AiSummaryPreviewRequest) => Promise<string | null>;
  onSaveAndRun?: (config: AiSummaryFieldConfig) => void;
  onSaveWithoutRunning?: (config: AiSummaryFieldConfig) => void;
}

const contentStyles = xcss({
  height: '389px'
});

const layoutStyles = xcss({
  columnGap: 'space.300',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  height: '100%'
});

const leftColumnStyles = xcss({
  backgroundColor: 'elevation.surface.sunken',
  padding: 'space.300'
});

const rightColumnStyles = xcss({
  backgroundColor: 'elevation.surface.sunken',
  padding: 'space.300'
});

const panelRadiusStyle = {
  borderRadius: token('radius.small', '4px')
};

const previewControlsStyles = xcss({
  width: '100%'
});

const previewSelectWrapperStyles = xcss({
  flexGrow: 1,
  minWidth: '0px'
});

const previewOutputStyles = xcss({
  whiteSpace: 'pre-wrap'
});

const previewMenuHeaderStyles = xcss({
  paddingBlock: 'space.100',
  paddingInline: 'space.150'
});

const compactSelectStyles = {
  control: (base: CSSObjectWithLabel, state: { isFocused: boolean }) => ({
    ...base,
    border: `1px solid ${
      state.isFocused ? token('color.border.focused') : token('color.border')
    }`,
    borderRadius: token('radius.medium', '6px'),
    height: '32px',
    minHeight: '32px'
  }),
  valueContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: `${token('space.0')} ${token('space.150')}`
  }),
  indicatorsContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    height: '100%'
  }),
  dropdownIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: `${token('space.0')} ${token('space.100')}`
  })
};

const AiSummaryFieldModal: FunctionComponent<AiSummaryFieldModalProps> = ({
  mode,
  initialConfig,
  previewOptions = EMPTY_PREVIEW_OPTIONS,
  onClose,
  onPreviewMenuOpen,
  onRegenerate,
  onGeneratePreview,
  onSaveAndRun,
  onSaveWithoutRunning
}) => {
  const { formatMessage } = useIntl();
  const [format, setFormat] = useState<AiSummaryOutputFormat>(
    initialConfig?.outputFormat ?? 'paragraph'
  );
  const [selectedPreviewOptionValue, setSelectedPreviewOptionValue] = useState<
    number | null
  >(previewOptions[0]?.value ?? null);
  const [previewState, setPreviewState] = useState<AiSummaryPreviewState>({
    kind: 'idle'
  });
  const previewAbortControllerRef = useRef<AbortController | null>(null);

  const formatOptions = useMemo<FormatOption[]>(
    () => [
      {
        label: formatMessage(messages['field-settings.ai-summary.format-one-liner']),
        value: 'one-liner'
      },
      {
        label: formatMessage(messages['field-settings.ai-summary.format-bullet-points']),
        value: 'bullet-points'
      },
      {
        label: formatMessage(messages['field-settings.ai-summary.format-paragraph']),
        value: 'paragraph'
      }
    ],
    [formatMessage]
  );

  const config = useMemo<AiSummaryFieldConfig>(
    () => ({
      outputFormat: format
    }),
    [format]
  );

  const selectedPreviewOption =
    previewOptions.find((option) => option.value === selectedPreviewOptionValue) ??
    previewOptions[0] ??
    null;

  const previewSelectComponents = useMemo(
    () => ({
      MenuList: (props: MenuListComponentProps<AiSummaryPreviewOption>) => (
        <selectComponents.MenuList {...props}>
          <Box xcss={previewMenuHeaderStyles}>
            <Text color="color.text.subtle" size="small" weight="bold">
              {formatMessage(
                messages['field-settings.ai-summary.preview-entries-header.non-final'],
                { count: Math.min(previewOptions.length, MAX_PREVIEW_ENTRY_COUNT) }
              )}
            </Text>
          </Box>
          {props.children}
        </selectComponents.MenuList>
      )
    }),
    [formatMessage, previewOptions.length]
  );

  const resetPreview = useCallback(() => {
    previewAbortControllerRef.current?.abort();
    previewAbortControllerRef.current = null;
    setPreviewState({ kind: 'idle' });
  }, []);

  useEffect(
    () => () => {
      previewAbortControllerRef.current?.abort();
      previewAbortControllerRef.current = null;
    },
    []
  );

  const handleFormatChange = useCallback(
    (option: ValueType<FormatOption, false>) => {
      if (option) {
        resetPreview();
        setFormat(option.value);
      }
    },
    [resetPreview]
  );

  const handlePreviewOptionChange = useCallback(
    (option: ValueType<AiSummaryPreviewOption, false>) => {
      resetPreview();
      setSelectedPreviewOptionValue(option?.value ?? null);
    },
    [resetPreview]
  );

  const handleGeneratePreview = useCallback(() => {
    if (!onGeneratePreview) {
      onRegenerate?.(format);
      return;
    }

    if (!selectedPreviewOption) {
      return;
    }

    previewAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    previewAbortControllerRef.current = abortController;
    setPreviewState({ kind: 'loading' });

    void onGeneratePreview({
      abortSignal: abortController.signal,
      config,
      entryId: selectedPreviewOption.entryId
    }).then(
      (summary) => {
        if (
          previewAbortControllerRef.current === abortController &&
          !abortController.signal.aborted
        ) {
          previewAbortControllerRef.current = null;
          setPreviewState(
            summary === null ? { kind: 'idle' } : { kind: 'success', summary }
          );
        }
      },
      () => {
        if (
          previewAbortControllerRef.current === abortController &&
          !abortController.signal.aborted
        ) {
          previewAbortControllerRef.current = null;
          setPreviewState({ kind: 'idle' });
        }
      }
    );
  }, [config, format, onGeneratePreview, onRegenerate, selectedPreviewOption]);

  const handleSaveWithoutRunning = useCallback(() => {
    onSaveWithoutRunning?.(config);
  }, [config, onSaveWithoutRunning]);

  const handleSaveAndRun = useCallback(() => {
    onSaveAndRun?.(config);
  }, [config, onSaveAndRun]);

  return (
    <ModalDialog
      width={968}
      onClose={onClose}
      shouldScrollInViewport
      testId="ai-summary-field-modal"
    >
      <ModalHeader hasCloseButton>
        <ModalTitle>
          <Inline alignBlock="center" space="space.100">
            <AiSummaryIcon label="" testId="ai-summary-field-icon" />
            <Heading as="h1" size="medium">
              {formatMessage(
                messages[
                  mode === 'edit'
                    ? 'field-settings.ai-summary.edit-modal-title'
                    : 'field-settings.ai-summary.modal-title'
                ]
              )}
            </Heading>
            {mode === 'add' && (
              <IconButton
                icon={InformationCircleIcon}
                appearance="subtle"
                spacing="compact"
                label={formatMessage(
                  messages['field-settings.ai-summary.modal-description']
                )}
                isTooltipDisabled={false}
                tooltip={{ position: 'bottom' }}
                testId="ai-summary-field-info"
              />
            )}
          </Inline>
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Box xcss={contentStyles}>
          <Box xcss={layoutStyles}>
            <Box xcss={leftColumnStyles} style={panelRadiusStyle}>
              <Stack space="space.250">
                <Heading as="h2" size="xsmall">
                  {formatMessage(
                    messages['field-settings.ai-summary.field-configuration']
                  )}
                </Heading>

                <Stack space="space.100">
                  <Label id="ai-summary-format-label" htmlFor="ai-summary-format">
                    <Text color="color.text.subtle" size="small" weight="bold">
                      {formatMessage(messages['field-settings.ai-summary.format'])}
                    </Text>
                  </Label>
                  <Select<FormatOption>
                    inputId="ai-summary-format"
                    options={formatOptions}
                    value={
                      formatOptions.find((option) => option.value === format) ?? null
                    }
                    onChange={handleFormatChange}
                    isSearchable={false}
                    styles={compactSelectStyles}
                  />
                </Stack>
                <Stack space="space.100">
                  <Inline alignBlock="center" space="space.050">
                    <Label id="ai-summary-triggers-label" htmlFor="ai-summary-triggers">
                      <Text color="color.text.disabled" size="small" weight="bold">
                        {formatMessage(messages['field-settings.ai-summary.triggers'])}
                      </Text>
                    </Label>
                    <Lozenge
                      appearance="default"
                      isBold={false}
                      spacing="spacious"
                      style={{ backgroundColor: token('color.background.neutral') }}
                      testId="ai-summary-coming-soon"
                    >
                      {formatMessage(messages['field-settings.ai-summary.coming-soon'])}
                    </Lozenge>
                  </Inline>
                  <Stack space="space.050">
                    <Checkbox
                      isDisabled
                      label={formatMessage(
                        messages['field-settings.ai-summary.apply-to-new-entries']
                      )}
                    />
                    <Checkbox
                      isDisabled
                      label={formatMessage(
                        messages['field-settings.ai-summary.apply-when-entry-edited']
                      )}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            <Box xcss={rightColumnStyles} style={panelRadiusStyle}>
              <Stack space="space.250">
                <Heading as="h2" size="xsmall">
                  {formatMessage(messages['field-settings.ai-summary.live-preview'])}
                </Heading>
                <Stack space="space.100">
                  <Label
                    id="ai-summary-preview-with-label"
                    htmlFor="ai-summary-preview-with"
                  >
                    <Text color="color.text.subtle" size="small" weight="bold">
                      {formatMessage(messages['field-settings.ai-summary.preview-with'])}
                    </Text>
                  </Label>
                  <Inline
                    alignBlock="center"
                    space="space.100"
                    xcss={previewControlsStyles}
                  >
                    <Box xcss={previewSelectWrapperStyles}>
                      <Select<AiSummaryPreviewOption>
                        inputId="ai-summary-preview-with"
                        components={previewSelectComponents}
                        options={previewOptions}
                        value={selectedPreviewOption}
                        onChange={handlePreviewOptionChange}
                        onMenuOpen={onPreviewMenuOpen}
                        formatOptionLabel={(option, { context }) =>
                          context === 'menu' && option.description ? (
                            <Stack space="space.050">
                              <Text maxLines={1}>{option.label}</Text>
                              <Text color="color.text.subtle" maxLines={1} size="small">
                                {option.description}
                              </Text>
                            </Stack>
                          ) : (
                            option.label
                          )
                        }
                        isSearchable={false}
                        styles={compactSelectStyles}
                      />
                    </Box>
                    {previewState.kind !== 'loading' && (
                      <Button
                        isDisabled={
                          !onRegenerate && (!selectedPreviewOption || !onGeneratePreview)
                        }
                        onClick={handleGeneratePreview}
                        style={{
                          borderRadius: token('radius.medium', '6px'),
                          color: token('color.text.subtle')
                        }}
                        testId="ai-summary-regenerate"
                      >
                        {formatMessage(messages['field-settings.ai-summary.regenerate'])}
                      </Button>
                    )}
                  </Inline>
                  {previewState.kind !== 'idle' && (
                    <Box
                      role="status"
                      testId="ai-summary-preview-status"
                      xcss={previewOutputStyles}
                    >
                      <Text>
                        {previewState.kind === 'loading'
                          ? formatMessage(
                              messages['field-settings.ai-summary.generating']
                            )
                          : previewState.summary}
                      </Text>
                    </Box>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Inline space="space.100" alignBlock="center">
          <Button
            appearance="subtle"
            onClick={handleSaveWithoutRunning}
            testId="ai-summary-save-without-running"
          >
            {formatMessage(messages['field-settings.ai-summary.save-without-running'])}
          </Button>
          <Button
            appearance="primary"
            onClick={handleSaveAndRun}
            testId="ai-summary-save-and-run"
          >
            {formatMessage(
              messages[
                mode === 'edit'
                  ? 'field-settings.ai-summary.save-and-rerun'
                  : 'field-settings.ai-summary.save-and-run'
              ]
            )}
          </Button>
        </Inline>
      </ModalFooter>
    </ModalDialog>
  );
};

export default AiSummaryFieldModal;
